/**
 * IframeRenderer - Isolated iframe-based WebEmbed renderer
 *
 * Renders custom HTML content in a fully isolated iframe with MessageChannel
 * communication. Supports legacy onboarding.online API for backward compatibility.
 *
 * Copied from V1 CustomScreen/IframeRenderer - rendering logic is schema-version agnostic.
 *
 * Security: Uses sandbox with allow-scripts, allow-same-origin, allow-forms, allow-popups,
 * allow-popups-to-escape-sandbox, allow-modals, allow-downloads, allow-storage-access-by-user-activation
 * for full WebEmbed support including OAuth popup flows and third-party storage access.
 *
 * @module screen-view-v2/components/screens/WebEmbed/IframeRenderer
 */

import { findCustomScreenChildSection } from '@modules/screen-view-v2/customScreenDataSources';
import { createPurchaseCompletion } from '@modules/screen-view-v2/components/screens/WebEmbed/createPurchaseCompletion';
import { useIframePurchaseSlots } from '@modules/screen-view-v2/components/screens/WebEmbed/useIframePurchaseSlots';

import React, { memo, useEffect, useRef, useMemo, useCallback, useState } from 'react';
import type { IframeRendererProps } from '../../../types/customScreenBridge.types';
import { useWebFontAssets } from '@modules/font-config/context/FontConfigContext';
import { buildWebFontFaceCss, getPreloadFace } from '@modules/font-config/utils/webFontCss';
import type { WebFontAsset } from '@modules/font-config/types/fontConfig.types';

/**
 * Setup script injected into iframe HTML.
 *
 * STUB PATTERN (Industry Standard - used by Amplitude, Google Analytics, Segment):
 * 1. SDK stub is created IMMEDIATELY (before any HTML code runs)
 * 2. Action methods (trackEvent, navigateNext) queue calls silently
 * 3. Data methods (getEmail, getScreenUserData) return undefined + console.warn
 * 4. When InitCustomScreen arrives: populate SDK, replay queued actions
 */
export const IFRAME_SETUP_SCRIPT = `
<script>
// ========================================================
// SEGMENTLY SDK STUB - Created IMMEDIATELY (Stub Pattern)
// Industry standard: Amplitude, Google Analytics, Segment
// ========================================================
(function() {
  'use strict';

  // Prevent double initialization
  if (window.__segmentlyIframeSetup) return;
  window.__segmentlyIframeSetup = true;

  // Detect if HTML already has legacy boilerplate (onboarding.online format)
  var hasLegacyBoilerplate = !!window.onboardingOnlineCustomScreenData;

  // ========================================================
  // STATE
  // ========================================================
  var isReady = false;
  var sdkData = null;
  var messagePort = null;
  var methodQueue = [];
  var dataWarningsShown = {};
  // V2 Variable system: local snapshot for same-screen read-after-write
  var variableDefinitions = {};
  var variableValues = {};
  var pendingVariableWrites = []; // Defense-in-depth: track writes for navigation payload
  // Email variable ID (from config or heuristic)
  var emailVariableId = null;
  // Variable change listeners (onVariableChange subscriptions)
  var variableListeners = {};   // variableId → [callback, ...]
  var wildcardListeners = [];   // callbacks for '*' subscriptions
  // Child sections data (from CustomEmbed containment)
  var childSectionsList = null;
  // Purchase callbacks (purchaseProduct requestId → { onSuccess, onCancel, onError })
  var purchaseCallbacks = {};
  var activePurchaseRequestId = null;
  var mountedPurchases = {};
  var createPurchaseCompletion = (${createPurchaseCompletion.toString()});

  // ========================================================
  // HELPER: Warn when data method called before ready
  // ========================================================
  function warnIfNotReady(methodName) {
    if (!isReady && !dataWarningsShown[methodName]) {
      dataWarningsShown[methodName] = true;
      console.warn(
        '[segmentlySDK] ' + methodName + '() called before SDK ready. ' +
        'Returning undefined. Use sdk.ready().then() or segmentlySDKReady event for data access.'
      );
    }
  }

  // ========================================================
  // HELPER: Queue action methods for later execution
  // ========================================================
  function queueMethod(method, args) {
    if (isReady && messagePort) {
      executeMethod(method, args);
    } else {
      methodQueue.push({ method: method, args: args });
    }
  }

  function executeMethod(method, args) {
    if (!messagePort) return;

    switch (method) {
      case 'trackEvent':
        messagePort.postMessage({ eventName: 'TrackEvent', args: { name: args.name, params: args.params } });
        break;
      case 'navigateNext':
        messagePort.postMessage({ eventName: 'LeaveCustomScreen', args: args.args || {} });
        break;
      case 'navigateBack':
        messagePort.postMessage({ eventName: 'BackScreen', args: null });
        break;
      case 'setValue':
        messagePort.postMessage({ eventName: 'SetValue', args: { key: args.key, value: args.value } });
        break;
      case 'setVariable':
        messagePort.postMessage({ eventName: 'SetVariable', args: { variableId: args.variableId, value: args.value } });
        break;
      case 'selectProduct':
        messagePort.postMessage({ eventName: 'SelectProduct', args: args });
        break;
      case 'purchaseProduct':
        messagePort.postMessage({ eventName: 'PurchaseProduct', args: args });
        break;
      case 'triggerButtonAction': {
        var buttonActionRequest = normalizeButtonActionRequest(args.sectionIdOrLabel);
        if (buttonActionRequest) {
          messagePort.postMessage({ eventName: 'TriggerButtonAction', args: buttonActionRequest });
        }
        break;
      }
      case 'triggerOptionAction': {
        var optionActionRequest = normalizeOptionActionRequest(args.sectionIdOrLabel, args.target);
        if (optionActionRequest) {
          messagePort.postMessage({ eventName: 'TriggerOptionAction', args: optionActionRequest });
        }
        break;
      }
    }
  }

  // ========================================================
  // VARIABLE CHANGE LISTENERS
  // ========================================================
  function fireVariableListeners(variableId, oldValue, newValue) {
    var event = { variableId: variableId, oldValue: oldValue, newValue: newValue };
    var specific = variableListeners[variableId];
    if (specific) {
      specific.forEach(function(cb) { try { cb(event); } catch(e) { console.error('[segmentlySDK] listener error:', e); } });
    }
    wildcardListeners.forEach(function(cb) { try { cb(event); } catch(e) { console.error('[segmentlySDK] listener error:', e); } });
  }

  // ========================================================
  // DATA LOOKUP HELPERS
  // ========================================================

  function lookupByScreenId(screenId, fieldPath) {
    if (!sdkData) return undefined;
    var userData = sdkData.userData || {};
    if (userData[screenId] && typeof userData[screenId] === 'object' && !Array.isArray(userData[screenId])) {
      var inputMatch = fieldPath.match(/^input\\['(.+?)'\\]\\.value$/);
      if (inputMatch) return userData[screenId][inputMatch[1]];
      if (userData[screenId][fieldPath] !== undefined) return userData[screenId][fieldPath];
    }
    var key = screenId + '.' + fieldPath;
    return userData[key];
  }

  function lookupByScreenName(screenName, fieldPath) {
    if (!sdkData) return undefined;
    var mapping = sdkData.screenMapping || {};
    var screenId = null;
    for (var id in mapping) {
      if (mapping[id] === screenName) { screenId = id; break; }
    }
    if (!screenId) return undefined;
    return lookupByScreenId(screenId, fieldPath);
  }

  function lookupScreenUserData(screenIdOrName) {
    if (!sdkData) return null;
    var mapping = sdkData.screenMapping || {};
    var userData = sdkData.userData || {};
    var results = {};
    var targetId = screenIdOrName;
    for (var id in mapping) {
      if (mapping[id] === screenIdOrName) { targetId = id; break; }
    }
    if (userData[targetId] && typeof userData[targetId] === 'object' && !Array.isArray(userData[targetId])) {
      return userData[targetId];
    }
    for (var key in userData) {
      if (key.indexOf(targetId + '.') === 0) {
        var fp = key.substring(targetId.length + 1);
        results[fp] = userData[key];
      }
    }
    return Object.keys(results).length > 0 ? results : null;
  }

  // ========================================================
  // EMAIL VARIABLE LOOKUP
  // ========================================================
  function getEmailCandidateIds() {
    var ids = [];
    if (emailVariableId) ids.push(emailVariableId);
    for (var vid in variableDefinitions) {
      var def = variableDefinitions[vid];
      if (!def) continue;
      var name = (def.name || '').toLowerCase();
      var id = (def.id || '').toLowerCase();
      if (name === 'email' || id === 'email' ||
          name === 'user_email' || id === 'user_email') {
        if (ids.indexOf(vid) === -1) ids.push(vid);
      }
    }
    if (typeof variableValues.email === 'string' && ids.indexOf('email') === -1) ids.push('email');
    if (typeof variableValues.user_email === 'string' && ids.indexOf('user_email') === -1) ids.push('user_email');
    return ids;
  }

  function findEmailVariableId() {
    var ids = getEmailCandidateIds();
    return ids.length ? ids[0] : null;
  }

  function findEmailVariableIdForWrite() {
    return findEmailVariableId() || 'email';
  }

  function getVariableDefinitionById(variableId) {
    if (variableDefinitions[variableId]) return variableDefinitions[variableId];
    for (var vid in variableDefinitions) {
      var def = variableDefinitions[vid];
      if (def && (def.id === variableId || def.name === variableId)) return def;
    }
    return null;
  }

  var resolveChildSection = (${findCustomScreenChildSection.toString()});
  function findChildSectionByIdOrLabel(nameOrId) {
    return resolveChildSection(childSectionsList, nameOrId);
  }

  function normalizeOptionActionRequest(sectionIdOrLabel, target) {
    var section = findChildSectionByIdOrLabel(sectionIdOrLabel);
    if (!section || !section.id) {
      console.warn('[segmentlySDK] triggerOptionAction: child section not found', sectionIdOrLabel);
      return null;
    }

    var request = { sectionId: section.id };
    if (typeof target === 'string' && target.length > 0) {
      request.optionId = target;
    } else if (typeof target === 'number' && isFinite(target)) {
      request.index = Math.floor(target);
    } else if (target && typeof target === 'object') {
      if (typeof target.optionId === 'string' && target.optionId.length > 0) {
        request.optionId = target.optionId;
      }
      if (typeof target.index === 'number' && isFinite(target.index)) {
        request.index = Math.floor(target.index);
      }
    }

    if (request.optionId === undefined && request.index === undefined) {
      console.warn('[segmentlySDK] triggerOptionAction: target must be an optionId or index', target);
      return null;
    }
    return request;
  }

  function normalizeButtonActionRequest(sectionIdOrLabel) {
    var section = findChildSectionByIdOrLabel(sectionIdOrLabel);
    if (!section || !section.id) {
      console.warn('[segmentlySDK] triggerButtonAction: child section not found', sectionIdOrLabel);
      return null;
    }
    if (section.kind !== 'Button') {
      console.warn('[segmentlySDK] triggerButtonAction: child section is not Button', sectionIdOrLabel);
      return null;
    }
    return { sectionId: section.id };
  }

  // ========================================================
  // CREATE STUB SDK IMMEDIATELY
  // ========================================================
  window.segmentlySDK = {
    isReady: function() { return isReady; },
    ready: function() {
      return new Promise(function(resolve) {
        if (isReady) { resolve(window.segmentlySDK); }
        else { window.addEventListener('segmentlySDKReady', function() { resolve(window.segmentlySDK); }, { once: true }); }
      });
    },
    screen: null,
    getEmail: function() {
      if (!isReady) { warnIfNotReady('getEmail'); return undefined; }
      // V2: try variable system first
      var eVarIds = getEmailCandidateIds();
      for (var i = 0; i < eVarIds.length; i++) {
        var eVarId = eVarIds[i];
        var val = variableValues[eVarId];
        if (typeof val === 'string') return val;
      }
      return undefined;
    },
    setEmail: function(value) {
      var eVarId = findEmailVariableIdForWrite();
      window.segmentlySDK.setVariable(eVarId, value);
    },
    getPlatformUserId: function() { if (!isReady) { warnIfNotReady('getPlatformUserId'); return undefined; } return sdkData.platformUserId || undefined; },
    getScreenUserData: function(s) { if (!isReady) { warnIfNotReady('getScreenUserData'); return undefined; } return lookupScreenUserData(s); },
    getUserDataByScreenId: function(sid, fp) { if (!isReady) { warnIfNotReady('getUserDataByScreenId'); return undefined; } return lookupByScreenId(sid, fp); },
    getUserDataByScreenName: function(sn, fp) { if (!isReady) { warnIfNotReady('getUserDataByScreenName'); return undefined; } return lookupByScreenName(sn, fp); },
    getCustomData: function(k) { if (!isReady) { warnIfNotReady('getCustomData'); return undefined; } return sdkData.customData ? sdkData.customData[k] : undefined; },
    getAllCustomData: function() { if (!isReady) { warnIfNotReady('getAllCustomData'); return {}; } return sdkData.customData || {}; },
    getSessionId: function() { if (!isReady) { warnIfNotReady('getSessionId'); return ''; } return (sdkData.webOnboarding && sdkData.webOnboarding.sessionId) || ''; },
    getCurrentScreenId: function() { if (!isReady) { warnIfNotReady('getCurrentScreenId'); return ''; } return sdkData.screenId || ''; },
    getScreenMapping: function() { if (!isReady) { warnIfNotReady('getScreenMapping'); return {}; } return sdkData.screenMapping || {}; },
    getCustomVariableDefault: function() { return undefined; },
    getCustomVariableValue: function(k) { if (!isReady) { warnIfNotReady('getCustomVariableValue'); return undefined; } return sdkData.customData ? sdkData.customData[k] : undefined; },
    getCustomVariableDefinitions: function() { if (!isReady) { warnIfNotReady('getCustomVariableDefinitions'); return []; } return sdkData.customVariableDefinitions || []; },
    getCustomVariableDefinition: function(k) {
      if (!isReady) { warnIfNotReady('getCustomVariableDefinition'); return null; }
      var defs = sdkData.customVariableDefinitions || [];
      for (var i = 0; i < defs.length; i++) { if (defs[i].key === k) return defs[i]; }
      return null;
    },
    trackEvent: function(name, params) { queueMethod('trackEvent', { name: name, params: params }); },
    navigateNext: function(args) {
      // V2: write args keys into matching variables before navigating
      if (args && typeof args === 'object') {
        for (var key in args) {
          if (key.charAt(0) === '_') continue; // skip internal keys like __pending*
          var varId = null;
          if (variableDefinitions[key]) {
            varId = key;
          } else {
            for (var vid in variableDefinitions) {
              var def = variableDefinitions[vid];
              if (def.name === key || def.id === key) { varId = vid; break; }
            }
          }
          if (varId) {
            window.segmentlySDK.setVariable(varId, args[key]);
          }
        }
      }
      // Defense-in-depth: embed pending variable writes in navigation payload
      var enrichedArgs = args || {};
      if (pendingVariableWrites.length > 0) {
        enrichedArgs.__pendingVariableWrites = pendingVariableWrites.slice();
        pendingVariableWrites = [];
      }
      queueMethod('navigateNext', { args: enrichedArgs });
    },
    navigateBack: function() { queueMethod('navigateBack', {}); },
    setValue: function(key, value) { queueMethod('setValue', { key: key, value: value }); },
    setCustomData: function(key, value) {
      if (isReady && sdkData) { if (!sdkData.customData) sdkData.customData = {}; sdkData.customData[key] = value; }
      queueMethod('setValue', { key: 'customData.' + key, value: value });
    },

    // ── V2 Variable System ──────────────────────────────────
    // Read current value of a variable (from local snapshot — same-screen read-after-write works)
    getVariable: function(variableId) {
      if (!isReady) { warnIfNotReady('getVariable'); return undefined; }
      return variableValues[variableId];
    },
    // Get variable definition (type, options, constraints)
    getVariableDefinition: function(variableId) {
      if (!isReady) { warnIfNotReady('getVariableDefinition'); return null; }
      return getVariableDefinitionById(variableId);
    },
    // Get all variable definitions
    getVariableDefinitions: function() {
      if (!isReady) { warnIfNotReady('getVariableDefinitions'); return {}; }
      return variableDefinitions;
    },
    // Get all variable values as a map
    getAllVariableValues: function() {
      if (!isReady) { warnIfNotReady('getAllVariableValues'); return {}; }
      var copy = {};
      for (var k in variableValues) { copy[k] = variableValues[k]; }
      return copy;
    },
    // Set variable value — updates local snapshot + sends to host
    // Type-aware: works with string, number, boolean, date, enum, enum[]
    setVariable: function(variableId, value) {
      var oldValue = variableValues[variableId];
      // Update local snapshot for immediate same-screen read-after-write
      variableValues[variableId] = value;
      // Track for defense-in-depth replay via navigation payload
      pendingVariableWrites.push({ variableId: variableId, value: value });
      // Send to host for persistent storage in V2InputManager
      queueMethod('setVariable', { variableId: variableId, value: value });
      // Fire listeners for local write (immediate, no host roundtrip)
      if (oldValue !== value) {
        fireVariableListeners(variableId, oldValue, value);
      }
    },
    // Subscribe to variable changes (local setVariable + host-pushed UpdateVariable)
    // Returns unsubscribe function. Use '*' to listen to all variables.
    onVariableChange: function(variableIdOrStar, callback) {
      if (typeof callback !== 'function') return function() {};
      if (variableIdOrStar === '*') {
        wildcardListeners.push(callback);
        return function() {
          var idx = wildcardListeners.indexOf(callback);
          if (idx >= 0) wildcardListeners.splice(idx, 1);
        };
      }
      if (!variableListeners[variableIdOrStar]) variableListeners[variableIdOrStar] = [];
      variableListeners[variableIdOrStar].push(callback);
      return function() {
        var arr = variableListeners[variableIdOrStar];
        if (arr) { var idx = arr.indexOf(callback); if (idx >= 0) arr.splice(idx, 1); }
      };
    },
    // Unsubscribe from variable changes (alternative to calling the returned unsubscribe fn)
    offVariableChange: function(variableIdOrStar, callback) {
      if (variableIdOrStar === '*') {
        var idx = wildcardListeners.indexOf(callback);
        if (idx >= 0) wildcardListeners.splice(idx, 1);
        return;
      }
      var arr = variableListeners[variableIdOrStar];
      if (arr) { var idx2 = arr.indexOf(callback); if (idx2 >= 0) arr.splice(idx2, 1); }
    },
    // ── Child Sections (Data Sources from CustomEmbed) ─────
    //
    // CRITICAL: Sections MUST be addressed by id or label (NEVER by kind), because
    // multiple sections of the same kind can coexist per screen. Kind-based lookup
    // returns a random first match and causes content bleed-through between fields.
    //
    // The ONLY exception is 'ProductCatalog' — the single canonical singleton per
    // screen. All other kinds (Text, Media, BulletList, SingleSelectionList, etc.)
    // require exact id or label match and return null on kind-based lookup.
    getChildSections: function() {
      if (!isReady) { warnIfNotReady('getChildSections'); return []; }
      return childSectionsList || [];
    },
    getChildSection: function(nameOrId) {
      if (!isReady) { warnIfNotReady('getChildSection'); return null; }
      return resolveChildSection(childSectionsList, nameOrId, true);
    },
    triggerOptionAction: function(sectionIdOrLabel, target) {
      queueMethod('triggerOptionAction', { sectionIdOrLabel: sectionIdOrLabel, target: target });
    },
    triggerButtonAction: function(sectionIdOrLabel) {
      queueMethod('triggerButtonAction', { sectionIdOrLabel: sectionIdOrLabel });
    },
    // Get text content (title/subtitle/subtitle2) from a child section by id or label.
    getChildSectionText: function(nameOrId) {
      if (!childSectionsList || !nameOrId) return null;
      var section = findChildSectionByIdOrLabel(nameOrId);
      if (!section || section.kind !== 'Text') return null;
      return { title: section.data.title || '', subtitle: section.data.subtitle || '', subtitle2: section.data.subtitle2 || '' };
    },
    // Get media content (url/type) from a child section by id or label.
    getChildSectionMedia: function(nameOrId) {
      if (!childSectionsList || !nameOrId) return null;
      var section = findChildSectionByIdOrLabel(nameOrId);
      if (!section || section.kind !== 'Media') return null;
      return { url: section.data.mediaUrl || '', type: section.data.mediaType || '' };
    },

    // ── Product convenience methods ─────────────────────────
    getProducts: function() {
      if (!isReady) { warnIfNotReady('getProducts'); return []; }
      var section = (childSectionsList || []).find(function(s) { return s.kind === 'ProductCatalog'; });
      return section ? (section.data.products || []) : [];
    },
    getProductPrice: function(productId) {
      if (!isReady) { warnIfNotReady('getProductPrice'); return null; }
      var section = (childSectionsList || []).find(function(s) { return s.kind === 'ProductCatalog'; });
      return section && section.data.productPrices ? section.data.productPrices[productId] : null;
    },
    getSelectedProduct: function() {
      if (!isReady) { warnIfNotReady('getSelectedProduct'); return null; }
      var section = (childSectionsList || []).find(function(s) { return s.kind === 'ProductCatalog'; });
      if (!section || !section.data.products || section.data.selectedProductIndex == null) return null;
      return section.data.products[section.data.selectedProductIndex] || null;
    },
    selectProduct: function(productIdOrIndex) {
      var section = (childSectionsList || []).find(function(s) { return s.kind === 'ProductCatalog'; });
      if (!section || !section.data.products) {
        console.warn('[segmentlySDK] selectProduct: no ProductCatalog child section');
        return;
      }
      var products = section.data.products;
      var index = -1;
      if (typeof productIdOrIndex === 'number') {
        index = productIdOrIndex;
      } else {
        for (var i = 0; i < products.length; i++) {
          if (products[i].productId === productIdOrIndex) { index = i; break; }
        }
      }
      if (index < 0 || index >= products.length) {
        console.warn('[segmentlySDK] selectProduct: invalid identifier', productIdOrIndex);
        return;
      }
      for (var j = 0; j < products.length; j++) { products[j].isSelected = (j === index); }
      section.data.selectedProductIndex = index;
      queueMethod('selectProduct', { childSectionId: section.id, productIndex: index, productId: products[index].productId });
    },

    // ── Purchase Product (programmatic Stripe payment) ────────
    mountPurchaseButton: function(targetOrSelector, productIdOrIndex, options) {
      var opts = options || {};
      var noop = function() {}; noop.requestClose = noop;
      var fallback = function() { try { if (typeof opts.onFallback === 'function') opts.onFallback(); } catch (e) {} };
      if (!isReady || activePurchaseRequestId || typeof window.__segmentlyMountPurchaseButton !== 'function') { fallback(); return noop; }
      var target;
      try { target = typeof targetOrSelector === 'string' ? document.querySelector(targetOrSelector) : targetOrSelector; }
      catch (e) { fallback(); return noop; }
      var section = (childSectionsList || []).find(function(s) {
        return s.kind === 'ProductCatalog' && s.data && Array.isArray(s.data.products) &&
          (typeof productIdOrIndex !== 'string' || s.data.products.some(function(p) { return p.productId === productIdOrIndex; }));
      });
      var products = section && section.data.products;
      var index = typeof productIdOrIndex === 'number' ? productIdOrIndex : products && products.findIndex(function(p) { return p.productId === productIdOrIndex; });
      var productId = Number.isSafeInteger(index) && index >= 0 && products && products[index] && products[index].productId;
      if (!target || target.getRootNode() !== document || !productId) { fallback(); return noop; }
      var requestId = 'mount_' + crypto.randomUUID();
      var requestClose = null;
      var closeBeforeRegistration = false;
      var settled = false;
      var settle = function(callback) {
        if (settled || !isReady || !window.frameElement || !window.frameElement.isConnected ||
            window.frameElement.contentDocument !== document) return;
        settled = true; callback();
      };
      var success = function() { settle(function() { if (typeof opts.onSuccess === 'function') opts.onSuccess(); }); };
      purchaseCallbacks[requestId] = {
        onSuccess: success,
        onCancel: function() { settle(function() { if (typeof opts.onCancel === 'function') opts.onCancel(); }); },
        onError: function(error) { settle(function() { if (typeof opts.onError === 'function') opts.onError(error); }); }
      };
      var retire = null;
      try { retire = window.__segmentlyMountPurchaseButton(target, {
        requestId: requestId, productId: productId, childSectionId: section.id,
        registerRequestClose: function(close) {
          requestClose = close;
          if (closeBeforeRegistration) {
            closeBeforeRegistration = false;
            if (!settled && isReady && mountedPurchases[requestId] && window.frameElement &&
              window.frameElement.isConnected && window.frameElement.contentDocument === document &&
              target.isConnected && target.getRootNode() === document) close();
          }
          return function() { if (requestClose === close) requestClose = null; };
        },
        onReady: function() { if (mountedPurchases[requestId] && typeof opts.onReady === 'function') opts.onReady(); },
        onFallback: fallback,
        onDispose: function() { if (mountedPurchases[requestId]) mountedPurchases[requestId](); },
        onRecoveredSuccess: function() {
          if (settled || !isReady || !window.frameElement || !window.frameElement.isConnected ||
              window.frameElement.contentDocument !== document) return;
          // Retire waiting offer replacements before the financial owner releases.
          Object.keys(mountedPurchases).forEach(function(id) { if (mountedPurchases[id]) mountedPurchases[id](); });
          delete purchaseCallbacks[requestId];
          success();
        },
        onRecoveredError: function(message) {
          if (settled || !isReady || !window.frameElement || !window.frameElement.isConnected ||
              window.frameElement.contentDocument !== document) return;
          // Verified unpaid: the waiting offer replacement keeps its slot and may prepare.
          delete purchaseCallbacks[requestId];
          settle(function() { if (typeof opts.onError === 'function') opts.onError({ error: message }); });
        }
      }); } catch (e) {}
      if (!retire) { delete purchaseCallbacks[requestId]; fallback(); return noop; }
      var dispose = function() {
        if (!mountedPurchases[requestId]) return;
        requestClose = null; closeBeforeRegistration = false;
        delete mountedPurchases[requestId]; delete purchaseCallbacks[requestId]; retire();
      };
      dispose.requestClose = function() {
        if (!settled && isReady && mountedPurchases[requestId] === dispose && window.frameElement &&
          window.frameElement.isConnected && window.frameElement.contentDocument === document &&
          target.isConnected && target.getRootNode() === document) {
          if (requestClose) requestClose();
          else closeBeforeRegistration = true;
        }
      };
      mountedPurchases[requestId] = dispose;
      return dispose;
    },
    purchaseProduct: function(productIdOrIndex, options) {
      options = options || {};
      var completion = createPurchaseCompletion(options);
      if (activePurchaseRequestId) {
        console.warn('[segmentlySDK] purchaseProduct: payment already in progress');
        completion.fail({ error: 'Payment already in progress' }, 'PURCHASE_IN_PROGRESS');
        return completion.promise;
      }
      var section = (childSectionsList || []).find(function(s) { return s.kind === 'ProductCatalog'; });
      if (!section || !section.data || !section.data.products) {
        console.warn('[segmentlySDK] purchaseProduct: no ProductCatalog child section');
        completion.fail({ error: 'No products available' }, 'NO_PRODUCTS');
        return completion.promise;
      }
      var products = section.data.products;
      var index = -1;
      if (typeof productIdOrIndex === 'number') {
        index = productIdOrIndex;
      } else {
        for (var i = 0; i < products.length; i++) {
          if (products[i].productId === productIdOrIndex) { index = i; break; }
        }
      }
      if (index < 0 || index >= products.length) {
        console.warn('[segmentlySDK] purchaseProduct: invalid product', productIdOrIndex);
        completion.fail({ error: 'Product not found' }, 'PRODUCT_NOT_FOUND');
        return completion.promise;
      }
      var requestId = 'pur_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6);
      activePurchaseRequestId = requestId;
      purchaseCallbacks[requestId] = completion;
      // Auto-select the product so host side has correct selection
      window.segmentlySDK.selectProduct(index);
      queueMethod('purchaseProduct', {
        requestId: requestId,
        productId: products[index].productId,
        childSectionId: section.id
      });
      return completion.promise;
    },

    debug: false
  };

  // ========================================================
  // LEGACY API STUBS
  // ========================================================
  var amplitudeProxy = {
    track: function(name, props) { window.segmentlySDK.trackEvent(name, props); },
    init: function() {},
    identify: function() {},
    setUserId: function() {},
    getInstance: function() { return amplitudeProxy; }
  };
  window.amplitude = amplitudeProxy;

  // ========================================================
  // SAFE PARENT PROXY
  // ========================================================
  (function setupParentProxy() {
    var realParent = null;
    var realParentAccessible = false;
    try {
      realParent = window.parent;
      if (realParent && realParent !== window) realParentAccessible = true;
    } catch (e) {}

    var needsProxy = false;
    try {
      if (!realParent || realParent === window) { needsProxy = true; }
      else { try { if (!realParent.amplitude) needsProxy = true; } catch (e) { needsProxy = true; } }
    } catch (e) { needsProxy = true; }

    if (needsProxy) {
      var safeParent = {
        amplitude: amplitudeProxy,
        postMessage: function(msg, origin) {
          try { if (realParentAccessible && realParent) realParent.postMessage(msg, origin || '*'); } catch (e) {}
        },
        location: { href: '' },
        parent: null
      };
      safeParent.parent = safeParent;
      try {
        Object.defineProperty(window, 'parent', { get: function() { return safeParent; }, set: function() {}, configurable: true });
      } catch (e) {}
    } else {
      try { if (realParent && !realParent.amplitude) realParent.amplitude = amplitudeProxy; } catch (e) {}
    }
  })();

  // ========================================================
  // INPUTS PROXY — bridges legacy inputs API to V2 variable system
  // Read:  inputs.selected_goals → variableValues['selected_goals']
  // Write: inputs.email = 'x'    → segmentlySDK.setVariable('email', 'x')
  // ========================================================
  var inputsProxy = (typeof Proxy !== 'undefined') ? new Proxy({}, {
    get: function(target, prop) {
      if (typeof prop !== 'string') return undefined;
      // Direct variable lookup by name/id
      if (variableValues[prop] !== undefined) return variableValues[prop];
      // Search by variable name (variableId may differ from name)
      for (var vid in variableDefinitions) {
        var def = variableDefinitions[vid];
        if (def.name === prop || def.id === prop) {
          return variableValues[vid];
        }
      }
      return target[prop];
    },
    set: function(target, prop, value) {
      if (typeof prop !== 'string') return true;
      // Find matching variable and set via SDK
      var targetVarId = null;
      if (variableDefinitions[prop]) {
        targetVarId = prop;
      } else {
        for (var vid in variableDefinitions) {
          var def = variableDefinitions[vid];
          if (def.name === prop || def.id === prop) { targetVarId = vid; break; }
        }
      }
      if (targetVarId && window.segmentlySDK) {
        window.segmentlySDK.setVariable(targetVarId, value);
      } else {
        target[prop] = value;
      }
      return true;
    },
    has: function(target, prop) {
      if (typeof prop !== 'string') return false;
      if (variableValues[prop] !== undefined) return true;
      for (var vid in variableDefinitions) {
        var def = variableDefinitions[vid];
        if (def.name === prop || def.id === prop) return variableValues[vid] !== undefined;
      }
      return prop in target;
    },
    ownKeys: function(target) {
      var keys = Object.keys(target);
      for (var vid in variableValues) {
        if (keys.indexOf(vid) < 0) keys.push(vid);
      }
      return keys;
    },
    getOwnPropertyDescriptor: function(target, prop) {
      if (typeof prop === 'string' && (variableValues[prop] !== undefined || prop in target)) {
        return { configurable: true, enumerable: true, value: variableValues[prop] || target[prop] };
      }
      return Object.getOwnPropertyDescriptor(target, prop);
    }
  }) : {};

  // ========================================================
  // LEGACY API
  // ========================================================
  if (!hasLegacyBoilerplate) {
    window.onboardingOnlineCustomScreenData = {
      inputs: inputsProxy, labels: {}, userData: {}, customData: {},
      webOnboarding: {}, incomeUserData: {}, language: '', screenId: ''
    };
    // Protect navigation globals from client-HTML overwrite (same pattern
    // as buildShadowDomSdk.ts — accessor descriptor with ignored setter).
    // See matching protection for shadow-DOM mode; in iframe mode the legacy
    // onmessage handler normally restores these via port, but defense-in-depth
    // here covers non-legacy HTMLs that still try to stub them out.
    var _leaveCustomScreenFn = function(args) { window.segmentlySDK.navigateNext(args); };
    var _backScreenFn = function() { window.segmentlySDK.navigateBack(); };
    try {
      Object.defineProperty(window, 'leaveCustomScreen', {
        get: function() { return _leaveCustomScreenFn; },
        set: function() { /* silently ignore client overwrite */ },
        configurable: true,
        enumerable: true
      });
      Object.defineProperty(window, 'backScreen', {
        get: function() { return _backScreenFn; },
        set: function() { /* silently ignore client overwrite */ },
        configurable: true,
        enumerable: true
      });
    } catch (e) {
      window.leaveCustomScreen = _leaveCustomScreenFn;
      window.backScreen = _backScreenFn;
    }
    window.getEmail = function() { return window.segmentlySDK.getEmail(); };
    window.getPlatformUserId = function() { return window.segmentlySDK.getPlatformUserId(); };
  }

  // ========================================================
  // RECEIVE DATA FROM HOST, POPULATE SDK, REPLAY QUEUE
  // ========================================================
  window.addEventListener('message', function(event) {
    if (event.data && event.data.eventName === 'InitCustomScreen') {
      var port = event.ports[0] || window.__segmentlyPort;
      if (event.ports[0]) window.__segmentlyPort = event.ports[0];
      var data = event.data.args;
      if (!port) return;

      sdkData = data;
      messagePort = port;
      port.start();

      // Listen for host-pushed messages (e.g., UpdateVariable for cross-section reactivity)
      port.onmessage = function(evt) {
        var msg = evt.data || {};
        if (msg.eventName === 'UpdateVariable' && msg.args) {
          var varId = msg.args.variableId;
          var newVal = msg.args.value;
          var oldVal = variableValues[varId];
          // Dedup: skip if value unchanged (echo from own setVariable)
          if (oldVal === newVal) return;
          if (Array.isArray(oldVal) && Array.isArray(newVal) &&
              oldVal.length === newVal.length && oldVal.every(function(v, i) { return v === newVal[i]; })) return;
          variableValues[varId] = newVal;
          fireVariableListeners(varId, oldVal, newVal);
        }
        // Handle PurchaseResult (host → iframe callback delivery)
        if (msg.eventName === 'PurchaseResult' && msg.args) {
          var rid = msg.args.requestId;
          var status = msg.args.status;
          if (status !== 'success' && status !== 'cancel' && status !== 'error') return;
          var cbs = purchaseCallbacks[rid];
          if (cbs) {
            delete purchaseCallbacks[rid];
            // Match the ordinary request before releasing its gate. Other results
            // may belong to retired requests or separately mounted payment slots.
            if (activePurchaseRequestId === rid) activePurchaseRequestId = null;
            if (mountedPurchases[rid]) mountedPurchases[rid]();
            try {
              if (status === 'success') { cbs.onSuccess(); }
              else if (status === 'cancel') { cbs.onCancel(); }
              else if (status === 'error') { cbs.onError({ error: msg.args.error || 'Unknown error' }); }
            } catch (e) {
              console.error('[segmentlySDK] purchaseProduct callback error:', e);
            }
          }
        }
      };

      window.segmentlySDK.screen = {
        screenId: data.screenId,
        screenName: data.screenName || '',
        labels: data.labels,
        values: data.values || {},
        userInputs: data.userData,
        customData: data.customData,
        locale: data.language,
        screenMapping: data.screenMapping || {},
        customVariableDefinitions: data.customVariableDefinitions || []
      };

      Object.assign(window.onboardingOnlineCustomScreenData, {
        inputs: inputsProxy,
        labels: data.labels,
        userData: data.userData,
        customData: data.customData,
        webOnboarding: data.webOnboarding,
        incomeUserData: data.incomeUserData,
        language: data.language,
        screenId: data.screenId,
        platformUserId: data.platformUserId || undefined
      });

      // Protect inputs proxy from being overwritten by legacy boilerplate
      // (e.g. Object.assign(data, event.data.args) where args.inputs = {})
      try {
        Object.defineProperty(window.onboardingOnlineCustomScreenData, 'inputs', {
          get: function() { return inputsProxy; },
          set: function() { /* ignore overwrites — proxy is the source of truth */ },
          configurable: true,
          enumerable: true
        });
      } catch(e) {}

      // Populate child sections from initData
      if (data.childSections) {
        childSectionsList = data.childSections;
      }

      // Populate email variable ID from config
      if (data.emailVariableId) {
        emailVariableId = data.emailVariableId;
      }

      // Populate V2 variable system from initData
      if (data.variableDefinitions) {
        variableDefinitions = data.variableDefinitions;
      }
      if (data.variableValues) {
        for (var vk in data.variableValues) {
          if (data.variableValues[vk] !== undefined) {
            variableValues[vk] = data.variableValues[vk];
          }
        }
      }

      isReady = true;

      if (methodQueue.length > 0) {
        methodQueue.forEach(function(item) { executeMethod(item.method, item.args); });
        methodQueue = [];
      }

      window.dispatchEvent(new CustomEvent('segmentlySDKReady', { detail: data }));
    }
  });

  // ========================================================
  // HEIGHT AUTO-REPORTING (ResizeObserver → postMessage)
  // ========================================================
  (function() {
    var lastHeight = 0;
    function reportHeight() {
      var h = document.documentElement.scrollHeight;
      if (h !== lastHeight) {
        lastHeight = h;
        try { window.parent.postMessage({ type: 'segmently-iframe-resize', height: h }, '*'); } catch(e) {}
      }
    }
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(reportHeight).observe(document.documentElement);
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', reportHeight);
    } else { reportHeight(); }
    window.addEventListener('load', reportHeight);
  })();
})();
</script>
`;

/**
 * Head markup for the project's active web fonts. The iframe is a separate
 * document — it does NOT see the parent page's @font-face declarations, so
 * the faces are re-declared inside the srcDoc's own <head>.
 */
function buildWebFontsHeadHtml(webFonts: WebFontAsset[]): string {
  if (!webFonts.length) return '';

  const parts: string[] = [];
  for (const asset of webFonts) {
    const face = getPreloadFace(asset);
    if (!face) continue;
    const integrity = face.sri ? ` integrity="${face.sri.replace(/"/g, '&quot;')}"` : '';
    parts.push(
      `<link rel="preload" href="${face.woff2.replace(/"/g, '&quot;')}" as="font" type="font/woff2" crossorigin${integrity}>`,
    );
  }
  const families = webFonts.map((asset) => asset.family).join(' ').replace(/"/g, '&quot;');
  parts.push(
    `<style data-segmently-web-fonts="${families}">${buildWebFontFaceCss(webFonts)}</style>`,
  );
  return parts.join('\n');
}

/**
 * Inject setup script (and optional extra head markup) into HTML content.
 */
function injectSetupScript(html: string, extraHead = ''): string {
  const headPayload = extraHead + IFRAME_SETUP_SCRIPT;

  if (!html) {
    return headPayload;
  }

  // Try to inject before </head>
  const headCloseMatch = html.match(/<\/head\s*>/i);
  if (headCloseMatch && headCloseMatch.index !== undefined) {
    return (
      html.slice(0, headCloseMatch.index) +
      headPayload +
      html.slice(headCloseMatch.index)
    );
  }

  // Try to inject after <head>
  const headOpenMatch = html.match(/<head[^>]*>/i);
  if (headOpenMatch && headOpenMatch.index !== undefined) {
    const insertPos = headOpenMatch.index + headOpenMatch[0].length;
    return html.slice(0, insertPos) + headPayload + html.slice(insertPos);
  }

  // Fallback: prepend to content
  return headPayload + html;
}

/**
 * IframeRenderer component for isolated WebEmbed rendering.
 *
 * Features:
 * - Full JavaScript and CSS isolation via iframe sandbox
 * - MessageChannel-based bidirectional communication
 * - Legacy onboarding.online API support
 * - Automatic cleanup of MessageChannel
 */
/**
 * Extended props for V2 IframeRenderer — adds variable system callback
 */
/** Variable update broadcast from host (cross-section reactivity) */
export interface VariableUpdateBroadcast {
  variableId: string;
  value: unknown;
  sourceIframeId?: string;
  _ts: number;
}

/** Purchase result to push back to iframe (from host payment flow) */
export interface PurchaseResultBroadcast {
  requestId: string;
  status: 'success' | 'cancel' | 'error';
  error?: string;
  _ts: number;
}

interface V2IframeRendererProps extends IframeRendererProps {
  /** Callback when iframe sets a variable value via sdk.setVariable() */
  onSetVariable?: (variableId: string, value: unknown) => void;
  /** Callback when iframe selects a product via sdk.selectProduct() */
  onSelectProduct?: (childSectionId: string, productIndex: number, productId: string) => void;
  /** Callback when iframe requests a purchase via sdk.purchaseProduct() */
  onPurchaseProduct?: (requestId: string, productId: string, childSectionId?: string) => void;
  /** Variable update to push to iframe (from cross-section broadcast) */
  lastVariableUpdate?: VariableUpdateBroadcast | null;
  /** Purchase result to push back to iframe (from host payment flow) */
  lastPurchaseResult?: PurchaseResultBroadcast | null;
  /** Opaque ground the embedded document is painted on (WP8 UX M-3). */
  embedGround?: string;
  /** Foreground that reads on `embedGround` (WP8 UX M-3). */
  embedForeground?: string;
}

/**
 * Base ground for the embedded document (WP8 UX M-3).
 *
 * An iframe is a separate document with a transparent body, so the funnel's canvas shows
 * through and custom HTML written for a light page renders unreadably on a dark funnel.
 * This paints an opaque ground and exposes it as tokens.
 *
 * It is written into a **cascade layer**: unlayered author styles always beat layered ones
 * whatever their order or specificity, so this is a default the custom HTML overrides by
 * simply declaring its own background — which is what "or set its own" means. Browsers
 * without `@layer` ignore the whole block, leaving today's behaviour.
 */
function buildEmbedGroundHtml(ground?: string, foreground?: string): string {
  if (!ground || !foreground) return '';
  return (
    '<style data-segmently-embed-ground>'
    + `:root{--sv2-embed-ground:${ground};--sv2-embed-foreground:${foreground};}`
    + `@layer segmently-embed-base{html,body{background-color:${ground};color:${foreground};}}`
    + '</style>'
  );
}

export const IframeRenderer = memo(function IframeRenderer({
  htmlContent,
  screenId,
  initData,
  onNavigate,
  onBack,
  onTrackEvent,
  onValueChange,
  onSetVariable,
  onTriggerOptionAction,
  onTriggerButtonAction,
  onSelectProduct,
  onPurchaseProduct,
  lastVariableUpdate,
  lastPurchaseResult,
  embedGround,
  embedForeground,
  className,
  style,
  autoResize = false,
}: V2IframeRendererProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const purchaseSlots = useIframePurchaseSlots(screenId, iframeRef);
  const channelRef = useRef<MessageChannel | null>(null);
  const [resizedHeight, setResizedHeight] = useState<number | null>(null);

  // Active web fonts must be re-declared inside the iframe's own document
  const webFontAssets = useWebFontAssets();

  // Prepare HTML with injected setup script for srcdoc
  const htmlWithScript = useMemo(() => {
    return injectSetupScript(
      htmlContent,
      buildEmbedGroundHtml(embedGround, embedForeground) + buildWebFontsHeadHtml(webFontAssets),
    );
  }, [htmlContent, webFontAssets, embedGround, embedForeground]);

  // Handle messages from iframe
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      const { eventName, args } = event.data || {};

      switch (eventName) {
        case 'LeaveCustomScreen':
        case 'NextScreen':
          onNavigate?.(args || {});
          break;
        case 'BackScreen':
          onBack?.();
          break;
        case 'TrackEvent':
          if (args?.name) {
            onTrackEvent?.(args.name, args.params);
          }
          break;
        case 'SetValue':
          if (args?.key !== undefined) {
            onValueChange?.(args.key, args.value);
          }
          break;
        case 'SetVariable':
          if (args?.variableId !== undefined) {
            onSetVariable?.(args.variableId, args.value);
          }
          break;
        case 'TriggerOptionAction':
          if (args?.sectionId !== undefined) {
            onTriggerOptionAction?.(args);
          }
          break;
        case 'TriggerButtonAction':
          if (args?.sectionId !== undefined) {
            onTriggerButtonAction?.(args);
          }
          break;
        case 'SelectProduct':
          if (args?.childSectionId !== undefined) {
            onSelectProduct?.(args.childSectionId, args.productIndex, args.productId);
          }
          break;
        case 'PurchaseProduct':
          if (args?.requestId && args?.productId) {
            onPurchaseProduct?.(args.requestId, args.productId, args.childSectionId);
          }
          break;
        default:
          break;
      }
    },
    [onNavigate, onBack, onTrackEvent, onValueChange, onSetVariable, onTriggerOptionAction, onTriggerButtonAction, onSelectProduct, onPurchaseProduct]
  );

  // Track port transfer state
  const portTransferredRef = useRef(false);
  const lastContentWindowRef = useRef<Window | null>(null);
  const lastSentDataRef = useRef<typeof initData | null>(null);

  // Setup MessageChannel when iframe loads
  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    purchaseSlots.bind();

    const currentContentWindow = iframe.contentWindow;
    const isSameWindow = currentContentWindow === lastContentWindowRef.current;

    // Skip if port already transferred and same window (duplicate fire)
    // But re-initialize for different windows (reload) or same window (back navigation)
    if (portTransferredRef.current && isSameWindow) {
      // Safe to re-send for back navigation
    }

    // Cleanup previous channel
    if (channelRef.current) {
      channelRef.current.port1.close();
    }

    portTransferredRef.current = false;
    lastContentWindowRef.current = currentContentWindow;

    // Create new MessageChannel
    const channel = new MessageChannel();
    channelRef.current = channel;
    channel.port1.onmessage = handleMessage;
    channel.port1.start();

    // Send InitCustomScreen if data is ready (including preview mode)
    if (initData && initData.screenId) {
      try {
        iframe.contentWindow.postMessage(
          { eventName: 'InitCustomScreen', args: initData },
          '*',
          [channel.port2]
        );
        portTransferredRef.current = true;
        lastSentDataRef.current = initData;
      } catch (e) {
        console.warn('[IframeRenderer] Port transfer failed, will retry on next load', e);
      }
    }
  }, [initData, handleMessage, purchaseSlots.bind]);

  // Auto-resize iframe to match content height (opt-in via autoResize prop)
  useEffect(() => {
    if (!autoResize) return;

    const onResize = (e: MessageEvent) => {
      if (e.data?.type !== 'segmently-iframe-resize') return;
      const iframe = iframeRef.current;
      if (!iframe || e.source !== iframe.contentWindow) return;
      const h = e.data.height;
      if (typeof h === 'number' && h > 0) {
        setResizedHeight(h);
      }
    };
    window.addEventListener('message', onResize);
    return () => window.removeEventListener('message', onResize);
  }, [autoResize]);

  // Cleanup MessageChannel on unmount
  useEffect(() => {
    return () => {
      if (channelRef.current) {
        channelRef.current.port1.close();
        channelRef.current = null;
      }
    };
  }, []);

  // Keep port1.onmessage in sync with latest handleMessage closure
  // Without this, when React re-renders change callback identities (e.g. onSetVariable),
  // port1.onmessage still references the stale closure — causing ~20% race condition
  useEffect(() => {
    if (channelRef.current) {
      channelRef.current.port1.onmessage = handleMessage;
    }
  }, [handleMessage]);

  // Reset port tracking when iframe content changes
  useEffect(() => {
    if (channelRef.current) {
      channelRef.current.port1.close();
      channelRef.current = null;
    }
    portTransferredRef.current = false;
    lastSentDataRef.current = null;
    lastContentWindowRef.current = null;
    setResizedHeight(null);
  }, [htmlWithScript]);

  // SSR hydration fix
  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe?.contentWindow && !channelRef.current) {
      handleIframeLoad();
    }
  }, [handleIframeLoad]);

  // Send InitCustomScreen when initData becomes available after iframe load
  // or when initData changes while port is already transferred (re-send without port)
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    if (!initData || !initData.screenId) return;

    if (!portTransferredRef.current) {
      // Port not yet transferred — send with port
      const channel = channelRef.current;
      if (!channel) return;

      try {
        iframe.contentWindow.postMessage(
          { eventName: 'InitCustomScreen', args: initData },
          '*',
          [channel.port2]
        );
        portTransferredRef.current = true;
        lastSentDataRef.current = initData;
        lastContentWindowRef.current = iframe.contentWindow;
      } catch (e) {
        console.warn('[IframeRenderer] Delayed port transfer failed', e);
      }
    } else if (initData !== lastSentDataRef.current) {
      // Port already transferred but data changed — re-send without port transfer
      // The iframe SDK uses saved window.__segmentlyPort as fallback
      try {
        iframe.contentWindow.postMessage(
          { eventName: 'InitCustomScreen', args: initData },
          '*'
        );
        lastSentDataRef.current = initData;
      } catch (e) {
        console.warn('[IframeRenderer] Re-send initData failed', e);
      }
    }
  }, [initData]);

  // Push variable updates from host to iframe (cross-section reactivity)
  useEffect(() => {
    if (!lastVariableUpdate) return;
    const channel = channelRef.current;
    if (!channel || !portTransferredRef.current) return;
    // Skip if this iframe was the source (avoid echo — SDK also deduplicates)
    if (lastVariableUpdate.sourceIframeId === screenId) return;
    try {
      channel.port1.postMessage({
        eventName: 'UpdateVariable',
        args: { variableId: lastVariableUpdate.variableId, value: lastVariableUpdate.value },
      });
    } catch (e) {
      console.warn('[IframeRenderer] Failed to push UpdateVariable', e);
    }
  }, [lastVariableUpdate, screenId]);

  // Push purchase result from host to iframe (callback delivery)
  useEffect(() => {
    if (!lastPurchaseResult) return;
    const channel = channelRef.current;
    if (!channel || !portTransferredRef.current) return;
    try {
      channel.port1.postMessage({
        eventName: 'PurchaseResult',
        args: {
          requestId: lastPurchaseResult.requestId,
          status: lastPurchaseResult.status,
          error: lastPurchaseResult.error,
        },
      });
    } catch (e) {
      console.warn('[IframeRenderer] Failed to push PurchaseResult', e);
    }
  }, [lastPurchaseResult]);

  const defaultStyle: React.CSSProperties = {
    width: '100%',
    height: autoResize ? 'auto' : '100%',
    border: 'none',
  };

  const mergedStyle = {
    ...defaultStyle,
    ...style,
    ...(autoResize && resizedHeight != null ? { height: resizedHeight } : {}),
  };

  return (
    <>
    <iframe
      ref={iframeRef}
      data-testid="web-embed-iframe"
      srcDoc={htmlWithScript}
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals allow-downloads allow-storage-access-by-user-activation"
      title={`Web Embed: ${screenId}`}
      className={className}
      style={mergedStyle}
      onLoad={handleIframeLoad}
    />
    {purchaseSlots.portal}
    </>
  );
});

export default IframeRenderer;
